'use client';

import { format } from 'date-fns';
import { match } from 'ts-pattern';
import { useCancelPurchaseOnetimePlan, useGetOnetimeOrderInfo, usePurchaseOnetimePlan } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { SpacePlanDto } from '@zep/types';
import { OnetimePaymentPaypleCallbackUrl } from '../../../../services';
import { stashGtmPurchaseAmount } from '../../../../utils/gtmMarketingEvent';
import { usePayple } from '../../subscription';
import { useExpectedOnetimePlanPrice, usePlanStatusType } from '../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanTemplateType } from '../types';
import { SharedPlanPaymentButton } from './shared';
export const PlanPaymentForeignOnetimeSubmit = () => {
  const {
    user
  } = useAuth();
  const {
    selectedPlanInfo,
    spaceHashId,
    isUpdate,
    planPaymentInfo,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch,
    getValues,
    handleSubmit
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const planTemplateType = watch('planTemplateType');
  const planStatusType = usePlanStatusType({
    planType
  });
  const onetimeStartDate = getValues('onetimeStartDate');
  const submitStartDate = format(onetimeStartDate, 'yyyy-MM-dd');
  const {
    showWidget
  } = usePayple();
  const {
    mutateAsync: purchaseOnetimePlan
  } = usePurchaseOnetimePlan();
  const {
    mutateAsync: getOrderInfo
  } = useGetOnetimeOrderInfo();
  const {
    mutateAsync: cancelPurchaseOnetimePlan
  } = useCancelPurchaseOnetimePlan();
  const {
    추가결제예상금액
  } = useExpectedOnetimePlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_ONETIME,
    spacePlanHashId: planPaymentInfo?.spacePlan?.spacePlanHashId ?? ''
  });
  const buttonPrice = match([isUpdate, planStatusType]).with([true, 'UP_GRADE'], () => 추가결제예상금액 < 0 ? 0 : 추가결제예상금액).otherwise(() => selectedPlanInfo.priceInfo?.finalPrice ?? selectedPlanInfo.salePrice);
  const isButtonDisabled = planType === 'enterprise' || isUpdate && planStatusType === 'SAME_GRADE' || isUpdate && planStatusType === 'DOWN_GRADE';
  const onSubmit = async () => {
    let spacePlanHashId = planPaymentInfo?.spacePlan?.spacePlanHashId ?? '';
    let orderInfoStatus = undefined;
    let orderInfo: {
      orderId: string;
    } | null = null;
    try {
      let purchaseRes: {
        status: number;
        statusKey: string;
        data: {
          spacePlan: SpacePlanDto;
        };
      };
      if (isUpdate) {
        // 일시납 업그레이드 시 spacePlanHashId 필요
        purchaseRes = await purchaseOnetimePlan({
          spaceHashId,
          spacePlanTemplateId: selectedPlanInfo.id,
          startDate: submitStartDate,
          spacePlanHashId
        });
      } else {
        // 일시납 처음 구매
        purchaseRes = await purchaseOnetimePlan({
          spaceHashId,
          spacePlanTemplateId: selectedPlanInfo.id,
          startDate: submitStartDate
        });
      }
      if (purchaseRes.status === 0 && purchaseRes.data.spacePlan?.spacePlanHashId) {
        spacePlanHashId = purchaseRes.data.spacePlan.spacePlanHashId;

        // 2. 주문 정보 요청
        const orderInfoRes = await getOrderInfo({
          spacePlanHashId
        });
        orderInfoStatus = orderInfoRes.status;
        orderInfo = orderInfoRes.data;
      }
      if (orderInfoStatus === 0 && !!orderInfo?.orderId) {
        // 완료 페이지의 Google Ads 구매 전환용으로 PG에 실제 전달되는 청구액
        // (업그레이드 시 추가결제금액)을 보관한다. Payple 리다이렉트로
        // 인메모리 값이 유실되기 때문이다. 국내 토스 일시납과 동일 패턴.
        stashGtmPurchaseAmount(orderInfo.orderId, {
          value: buttonPrice,
          currency: selectedPlanInfo.currency || 'USD'
        });
        await showWidget({
          type: 'onetime',
          orderId: orderInfo?.orderId,
          comments: 'Onetime Plan Payment',
          totalAmount: buttonPrice,
          playerEmail: user?.snsId || '',
          callbackUrl: OnetimePaymentPaypleCallbackUrl,
          spacePlanHashId: purchaseRes.data.spacePlan?.spacePlanHashId ?? '',
          zipCode: getValues('zipCode') ?? ''
        });
      }
    } catch (e) {
      console.error(e);
      await cancelPurchaseOnetimePlan({
        spaceHashId,
        spacePlanHashId: spacePlanHashId || ''
      });
    }
  };
  return <SharedPlanPaymentButton isButtonDisabled={isButtonDisabled} buttonPrice={buttonPrice} currencyString={currencyString} onClick={handleSubmit(onSubmit)} data-sentry-element="SharedPlanPaymentButton" data-sentry-component="PlanPaymentForeignOnetimeSubmit" data-sentry-source-file="PlanPaymentForeignOnetimeSubmit.tsx" />;
};